import { valibotResolver } from "@hookform/resolvers/valibot";
import { useLayoutEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { useActiveConversationContext } from "../context/ActiveConversationContext";
import useConversationMessagesUpdates from "../datahooks/useConversationMessagesUpdates";
import useConversationUpdates from "../datahooks/useConversationUpdates";
import { InboxMessageSchema, type InboxMessageSchemaType } from "../schemas";
import useActiveConversationOwnerAccount from "./useActiveConversationOwnerAccount";
import useActiveConversationState from "./useActiveConversationState";
import useInboxDraftStore from "./useInboxDraftStore";
import { useNewConversation } from "./useNewConversation";

import type React from "react";
import type { ConversationMessage } from "../types";

function loadCtrlToSend() {
  const savedCtrlToSend = localStorage.getItem("ctrlToSend");
  if (savedCtrlToSend) {
    return JSON.parse(savedCtrlToSend);
  }
  return true;
}

export default function useMessageField(
  hasMessageFromAnotherUser: boolean,
  editMessage: ConversationMessage,
  onSuccess: () => void,
) {
  const { activeConversation } = useActiveConversationContext();
  const { setActiveConversationState } = useActiveConversationState();
  const { newConversation } = useNewConversation();

  const [ctrlToSend, setCtrlToSend] = useState(loadCtrlToSend());

  const formReturn = useForm<InboxMessageSchemaType>({
    resolver: valibotResolver(InboxMessageSchema),
    defaultValues: {
      message: "",
    },
  });
  const { setValue, getValues, reset, handleSubmit, subscribe } = formReturn;

  const { sendMessage, isSendingMessage } = useConversationUpdates();
  const { updateMessage, isUpdatingMessage } = useConversationMessagesUpdates({
    conversationUrn: activeConversation
      ? activeConversation.conversation_urn
      : "",
  });
  const { ownerAccount } = useActiveConversationOwnerAccount();

  const isLoading = isSendingMessage || isUpdatingMessage;

  const onSubmit = handleSubmit((data) => {
    //editing action
    if (editMessage && "message" in data) {
      const { message } = data;
      if (message.trim() !== editMessage.body) {
        updateMessage({
          accountId: activeConversation.owner,
          message,
          messageUrn: editMessage.urn,
        }).then(onSuccess);
      }
    } else {
      sendMessage(
        activeConversation
          ? {
              accountId: activeConversation.owner,
              recipients: activeConversation.participants,
              conversationUrn: activeConversation.conversation_urn,
              data,
            }
          : {
              accountId: newConversation.owner,
              recipients: [newConversation.participant],
              data,
            },
      ).then(({ conversationUrn }) => {
        if (newConversation) {
          setActiveConversationState(
            conversationUrn,
            newConversation.owner,
            undefined,
          );
        }
        reset({ message: "" });
        onSuccess();
      });
    }
  });

  function onKeyDown(e: React.KeyboardEvent) {
    if (!isLoading && e.key === "Enter") {
      if (e.ctrlKey || e.metaKey) {
        if (ctrlToSend) {
          onSubmit();
        } else {
          setValue("message", `${getValues("message")}\n`);
        }
      } else if (!ctrlToSend) {
        onSubmit();
        e.preventDefault();
      }
    }
  }

  //draft management logic
  const { drafts, setDraft } = useInboxDraftStore();

  const draftKey = activeConversation
    ? activeConversation.conversation_urn
    : `new:from=${newConversation.owner},to=${newConversation.participant.id}`;

  useLayoutEffect(() => {
    return subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        if ("message" in values && !editMessage) {
          setDraft(draftKey, values.message);
        }
      },
    });
  }, [subscribe, draftKey]);

  useLayoutEffect(() => {
    if (activeConversation) {
      reset({ message: drafts[draftKey] || "" });
    }
  }, [activeConversation]);

  //messaging disabled logic
  let isMessagingDisabled = isLoading;

  if (activeConversation) {
    const { participants, connected, last_message } = activeConversation;

    // Disable messaging if recipient isn’t a lead, not connected, or last message was declined AND there is no incoming message
    if (
      !hasMessageFromAnotherUser &&
      ((!participants[0].is_lead && !connected) || last_message.declined)
    ) {
      isMessagingDisabled = true;
    }
  }
  if (ownerAccount) {
    const { license, state, type } = ownerAccount;
    // Messaging is blocked if disabled with user is not logged in or has no license
    if (state !== "LoggedIn" || (!license && type !== "rental")) {
      isMessagingDisabled = true;
    }
  }
  return {
    formReturn,
    onSubmit,
    onKeyDown,
    isLoading,
    ctrlToSend,
    setCtrlToSend,
    isMessagingDisabled,
  };
}
